import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { ProfilePicture } from "../../../../../components";
import {
  useCurrentDate,
  Button,
  DataButton,
  Icon,
} from "@sellar-io/components";
import {
  pluralize,
  getDropStatus,
  getDaysUntilDropEnds,
  type DropStatus,
} from "@sellar-io/common";
import { useBuyerRelationshipList } from "../../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useToast } from "../../../../../components/toast/useToast.shared";
import { Toast } from "../../../../../components/toast/Toast";
import { useBuyerRelationshipCreate } from "../../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipCreate";
import { useBuyerRelationshipFavouriteDelete } from "../../../../../hooks/api/v2/buyerRelationshipFavourite/useBuyerRelationshipFavouriteDelete";
import { useBuyerRelationshipFavouriteCreate } from "../../../../../hooks/api/v2/buyerRelationshipFavourite/useBuyerRelationshipFavouriteCreate";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { Alert } from "../../../../../components/alert/Alert";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useMediaQuery } from "usehooks-ts";

const calculateTimeLeft = (releaseDate: Date) => {
  const timeDifference = dayjs(releaseDate).diff(dayjs());

  if (timeDifference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(timeDifference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((timeDifference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((timeDifference / 1000 / 60) % 60),
    seconds: Math.floor((timeDifference / 1000) % 60),
  };
};

const getToastCopy = (dropStatus: DropStatus) => {
  if (dropStatus === "prelaunch") {
    return "We'll remind you when the event goes live";
  }

  return "We'll remind you when the event returns";
};

const useSubscribeToSupplierStorefrontAvailabilityUpdates = (params: {
  supplierId: number;
  relationshipId: number | null;
}) => {
  const createRelationMutation = useBuyerRelationshipCreate();
  const addToFavouriteMutation = useBuyerRelationshipFavouriteCreate();

  const buyerRelationshipId = params.relationshipId;

  const isPending =
    createRelationMutation.isPending || addToFavouriteMutation.isPending;

  if (buyerRelationshipId) {
    return {
      subscribe: async () => {
        return addToFavouriteMutation.mutateAsync({
          buyerRelationshipId,
        });
      },
      isPending,
    };
  }

  return {
    subscribe: async () => {
      return createRelationMutation
        .mutateAsync({
          SupplierId: params.supplierId,
        })
        .then((result) => {
          return addToFavouriteMutation.mutateAsync({
            buyerRelationshipId: result.id,
          });
        });
    },
    isPending,
  };
};

export const RemoveFromFavouriteListDialog = ({
  onDismiss,
  active,
  buyerRelationshipFavouriteId,
}: {
  onDismiss: () => void;
  active: boolean;
  buyerRelationshipFavouriteId: number;
}) => {
  const deleteFromFavouriteListMutation = useBuyerRelationshipFavouriteDelete(
    buyerRelationshipFavouriteId,
  );

  const onRemove = () => {
    deleteFromFavouriteListMutation.mutate(undefined, {
      onSuccess: () => {
        onDismiss();
      },
    });
  };
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Remove from list?"}
      />
      <DialogContent>
        {deleteFromFavouriteListMutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(deleteFromFavouriteListMutation.error)?.response
                ?.data.message ?? "Unknown error"
            }
          />
        ) : null}
      </DialogContent>
      <DialogCTA>
        <Button
          loading={deleteFromFavouriteListMutation.isPending}
          color={"red"}
          onClick={onRemove}
        >
          Remove from favourite list
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const ReleaseCountdownTimer = ({ releaseDate }: { releaseDate: Date }) => {
  const [timeLeft, setTimeLeft] = useState(() =>
    calculateTimeLeft(releaseDate),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(releaseDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [releaseDate]);

  return (
    <div className="flex flex-col items-center px-4">
      <div className="flex space-x-2 text-center text-lg">
        <div>
          <div className="text-xl font-bold text-zinc-800">{timeLeft.days}</div>
          <div className="text-sm font-semibold text-zinc-500">Days</div>
        </div>
        <div className="max-h-5 border-l border-zinc-300" />
        <div>
          <div className="text-xl font-bold text-zinc-800">
            {timeLeft.hours}
          </div>
          <div className="text-sm font-semibold text-zinc-500">Hours</div>
        </div>
        <div className="max-h-5 border-l border-zinc-300" />
        <div>
          <div className="text-xl font-bold text-zinc-800">
            {timeLeft.minutes}
          </div>
          <div className="text-sm font-semibold text-zinc-500">Minutes</div>
        </div>
        <div className="max-h-5 border-l border-zinc-300" />
        <div>
          <div className="text-xl font-bold text-zinc-800">
            {timeLeft.seconds}
          </div>
          <div className="text-sm font-semibold text-zinc-500">Seconds</div>
        </div>
      </div>
    </div>
  );
};

const DropEndNotice = ({
  endDate,
  hasDropNotificationsEnabled,
}: {
  endDate: Date;
  hasDropNotificationsEnabled: boolean;
}) => {
  const currentDate = useCurrentDate();
  const now = currentDate;
  const daysDifference = getDaysUntilDropEnds(now, endDate);
  const isDropOver =
    getDropStatus(now, { startDate: null, endDate }) === "ended";
  const endTimeFormatted = dayjs(endDate).format("h:mma");

  const { title, body } = useMemo(() => {
    if (isDropOver) {
      if (hasDropNotificationsEnabled) {
        return {
          title: `You’re all set — we’ll notify you when the next drop goes live.`,
          body: "",
        };
      }

      return {
        title: `Don't miss out on the next drop`,
        body: "Get notified when it goes live",
      };
    }

    if (daysDifference === 0) {
      return {
        title: "Last day to order",
        body: `Get your order in before ${endTimeFormatted} today`,
      };
    }

    return {
      title: `${daysDifference} ${pluralize("day", daysDifference)} left to order`,
      body: `Get your order in before ${endTimeFormatted} on the last day`,
    };
  }, [
    daysDifference,
    isDropOver,
    hasDropNotificationsEnabled,
    endTimeFormatted,
  ]);

  return (
    <div className="flex max-w-sm flex-col px-2">
      <div className="flex space-x-4 text-start">
        <div>
          <div className="text-xl font-bold text-zinc-800">{title}</div>
          <div className="text-sm text-zinc-600">{body}</div>
        </div>
      </div>
    </div>
  );
};

const StorefrontBannerComponent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const sellarLogoBgV2Url =
    "https://sellar.imgix.net/sellar-image-assets/sellar-logo-bg-v2.png";

  return (
    <div
      className="flex items-center justify-center border-t border-b border-solid bg-zinc-50 bg-repeat"
      style={{
        backgroundImage: `url(${sellarLogoBgV2Url})`,
        backgroundSize: "70px 70px",
      }}
    >
      {children}
    </div>
  );
};

const NotificationButton = ({
  hasDropNotificationsEnabled,
  onClick,
  isLoading,
}: {
  hasDropNotificationsEnabled: boolean;
  onClick: () => void;
  isLoading: boolean;
}) => {
  return (
    <DataButton
      active={false}
      className={"w-fit"}
      onClick={() => onClick()}
      border={true}
      loading={isLoading}
      disabled={isLoading}
    >
      {hasDropNotificationsEnabled && (
        <>
          <Icon icon={"BELL_RING"} className={"text-grey-700"} size={0.7} />
        </>
      )}

      {!hasDropNotificationsEnabled && (
        <>
          <Icon icon={"BELL_OUTLINE"} className={"text-grey-700"} size={0.7} />
          Notify me
        </>
      )}
    </DataButton>
  );
};

const LearnMoreLink = () => {
  return (
    <a
      href="https://intercom.help/sellario/en/articles/12257400-buyer-guide-limited-drops"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 px-0 py-0.5 text-sm font-semibold text-zinc-700 hover:bg-transparent"
    >
      <span>Learn more</span>
      <Icon icon="CHEVRON_RIGHT" className="relative bottom-[1px]" size={0.7} />
    </a>
  );
};

const SupplierAvailabilityBadge = ({
  supplierLogoUrl,
  supplierName,
  availableFromDate,
  availableUntilDate,
  dropStatus,
}: {
  supplierLogoUrl: string;
  supplierName: string;
  availableFromDate: Date;
  availableUntilDate: Date;
  dropStatus: DropStatus;
}) => {
  return (
    <div className="flex gap-2 px-2">
      <ProfilePicture
        logoUrl={supplierLogoUrl}
        name={supplierName}
        size={"large"}
        fallback="supplier-badge"
      />
      <div className="flex flex-col justify-start">
        <div className="text-xl font-semibold text-zinc-800">Limited drop</div>
        <div className="flex items-center gap-1 text-sm text-zinc-600">
          {dropStatus === "prelaunch" && (
            <>
              <Icon icon={"CIRCLE"} color={"orange"} size={0.7} />
              <div>
                Releases {dayjs(availableFromDate).format("HH:mm DD/MM/YY")}
              </div>
            </>
          )}
          {dropStatus === "live" && (
            <>
              <Icon icon={"CIRCLE"} color={"green"} size={0.7} />
              <div>
                Active until{" "}
                {dayjs(availableUntilDate).format("HH:mm DD/MM/YY")}
              </div>
            </>
          )}

          {dropStatus === "ended" && (
            <>
              <Icon icon={"CIRCLE"} color={"red"} size={0.7} />
              <div>Ended {dayjs(availableUntilDate).format("DD/MM/YY")}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StorefrontHeaderContent = ({
  dropStatus,
  isBuyerSubscribedToStorefrontUpdates,
  availableFromDate,
  availableEndDate,
  supplier,
  onNotificationButtonClick,
  isLoading,
}: {
  dropStatus: DropStatus;
  isBuyerSubscribedToStorefrontUpdates: boolean;
  availableFromDate: Date;
  availableEndDate: Date;
  supplier: {
    logoUrl: string;
    name: string;
    id: number;
  };
  onNotificationButtonClick: () => void;
  isLoading: boolean;
}) => {
  const isDesktop = useMediaQuery("(min-width: 900px)");

  const [isExpanded, setIsExpanded] = useState(false);

  if (isDesktop) {
    return (
      <div className="flex w-auto flex-row flex-wrap items-center gap-2 p-4">
        <SupplierAvailabilityBadge
          availableFromDate={availableFromDate}
          availableUntilDate={availableEndDate}
          dropStatus={dropStatus}
          supplierLogoUrl={supplier.logoUrl}
          supplierName={supplier.name}
        />

        {dropStatus === "prelaunch" && (
          <ReleaseCountdownTimer releaseDate={availableFromDate} />
        )}

        {["live", "ended"].includes(dropStatus) && (
          <>
            <div className="h-10 border-l border-zinc-300" />
            <DropEndNotice
              endDate={availableEndDate}
              hasDropNotificationsEnabled={isBuyerSubscribedToStorefrontUpdates}
            />
          </>
        )}

        <div className="flex items-center gap-4">
          <NotificationButton
            hasDropNotificationsEnabled={isBuyerSubscribedToStorefrontUpdates}
            onClick={() => onNotificationButtonClick()}
            isLoading={isLoading}
          />

          <LearnMoreLink />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        className="mx-auto flex max-w-[500px] cursor-pointer items-center justify-between p-4"
        onClick={() => {
          setIsExpanded((current) => !current);
        }}
      >
        <SupplierAvailabilityBadge
          availableFromDate={availableFromDate}
          availableUntilDate={availableEndDate}
          dropStatus={dropStatus}
          supplierLogoUrl={supplier.logoUrl}
          supplierName={supplier.name}
        />
        <Icon icon={isExpanded ? "CHEVRON_UP" : "CHEVRON_DOWN"} size={1} />
      </div>

      {isExpanded && (
        <>
          <div className="mx-4 border-b border-zinc-300" />

          <div className="mx-auto flex max-w-[500px] flex-col gap-4 p-4">
            {dropStatus === "prelaunch" ? (
              <ReleaseCountdownTimer releaseDate={availableFromDate} />
            ) : (
              <DropEndNotice
                endDate={availableEndDate}
                hasDropNotificationsEnabled={
                  isBuyerSubscribedToStorefrontUpdates
                }
              />
            )}

            <div className="flex w-full items-center justify-center gap-4">
              <NotificationButton
                hasDropNotificationsEnabled={
                  isBuyerSubscribedToStorefrontUpdates
                }
                onClick={() => onNotificationButtonClick()}
                isLoading={isLoading}
              />

              <LearnMoreLink />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const StorefrontSupplierAvailabilityBanner = ({
  availableFromDate,
  availableEndDate,
  supplier,
}: {
  availableFromDate: Date;
  availableEndDate: Date;
  supplier: {
    logoUrl: string;
    name: string;
    id: number;
  };
}) => {
  const currentDate = useCurrentDate();
  const { toast } = useToast();

  const [
    deleteFavouriteRelationshipDialogActive,
    setDeleteFavouriteRelationshipDialogActive,
  ] = useState<boolean>(false);

  const dropStatus = getDropStatus(currentDate, {
    startDate: availableFromDate,
    endDate: availableEndDate,
  });

  const buyerRelationshipListQuery = useBuyerRelationshipList(
    {
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: supplier.id,
        },
      ],
      limit: 1,
    },

    {
      enabled: !!supplier.id,
    },
  );

  const buyerRelationship = buyerRelationshipListQuery.data?.pages[0]?.[0];

  const {
    subscribe: subscribeToStorefrontUpdates,
    isPending: subscribeToStorefrontUpdatesPending,
  } = useSubscribeToSupplierStorefrontAvailabilityUpdates({
    relationshipId: buyerRelationship?.id ?? null,
    supplierId: supplier.id,
  });

  const isBuyerSubscribedToStorefrontUpdates =
    !!buyerRelationship?.BuyerRelationshipFavourite;

  const isLoading =
    buyerRelationshipListQuery.isPending || subscribeToStorefrontUpdatesPending;

  const onClickNotificationButton = async () => {
    if (isBuyerSubscribedToStorefrontUpdates) {
      setDeleteFavouriteRelationshipDialogActive(true);
    }

    return subscribeToStorefrontUpdates().then(() =>
      toast({
        placement: "bottom",
        render: () => (
          <Toast icon="BELL_RING">{getToastCopy(dropStatus)}</Toast>
        ),
      }),
    );
  };

  return (
    <>
      <StorefrontBannerComponent>
        <StorefrontHeaderContent
          dropStatus={dropStatus}
          isBuyerSubscribedToStorefrontUpdates={
            isBuyerSubscribedToStorefrontUpdates
          }
          availableFromDate={availableFromDate}
          availableEndDate={availableEndDate}
          supplier={supplier}
          onNotificationButtonClick={onClickNotificationButton}
          isLoading={isLoading}
        />
      </StorefrontBannerComponent>
      <RemoveFromFavouriteListDialog
        onDismiss={() => {
          setDeleteFavouriteRelationshipDialogActive(false);
        }}
        active={deleteFavouriteRelationshipDialogActive}
        buyerRelationshipFavouriteId={
          buyerRelationship?.BuyerRelationshipFavourite?.id ?? 0
        }
      />
    </>
  );
};
